import React, { useEffect, useRef, useState } from "react";
import { Upload, Star, X, GripVertical, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { api, API, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

export default function PhotoGallery({ propertyId, readOnly = false }) {
  const [photos, setPhotos] = useState([]);
  const [dragIdx, setDragIdx] = useState(null);
  const [captionEdit, setCaptionEdit] = useState({});
  const fileRef = useRef();

  const load = async () => {
    const { data } = await api.get(`/properties/${propertyId}/photos`);
    setPhotos(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [propertyId]);

  const uploadFiles = async (files) => {
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/properties/${propertyId}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      } catch (e) { toast.error(formatApiError(e)); }
    }
    toast.success(`${files.length} foto caricate`);
    load();
  };

  const onFileInput = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) uploadFiles(files);
    e.target.value = "";
  };

  const setCover = async (id) => {
    try { await api.patch(`/properties/${propertyId}/photos/${id}/cover`); toast.success("Copertina impostata"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Rimuovere questa foto?")) return;
    try { await api.delete(`/properties/${propertyId}/photos/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const saveCaption = async (id) => {
    try {
      await api.patch(`/properties/${propertyId}/photos/${id}`, { caption: captionEdit[id] ?? "" });
      setCaptionEdit((s) => { const n = { ...s }; delete n[id]; return n; });
      load(); toast.success("Didascalia salvata");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onDragStart = (i) => setDragIdx(i);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = async (dstIdx) => {
    if (dragIdx === null || dragIdx === dstIdx) return;
    const arr = photos.slice();
    const [moved] = arr.splice(dragIdx, 1);
    arr.splice(dstIdx, 0, moved);
    setPhotos(arr);
    setDragIdx(null);
    try { await api.post(`/properties/${propertyId}/photos/reorder`, { photo_ids: arr.map((p) => p.id) }); }
    catch (e) { toast.error(formatApiError(e)); load(); }
  };

  return (
    <div className="space-y-4" data-testid="photo-gallery">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <ImageIcon className="w-4 h-4" />
          {photos.length} foto {photos.length > 0 && !readOnly && "· trascina per riordinare"}
        </div>
        {!readOnly && <>
          <input ref={fileRef} type="file" multiple accept="image/*" className="hidden" onChange={onFileInput} data-testid="photo-file-input" />
          <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} className="gap-2" data-testid="upload-photos-button">
            <Upload className="w-4 h-4" /> Carica foto
          </Button>
        </>}
      </div>

      {photos.length === 0 ? (
        <div className="border border-dashed border-border rounded-md p-10 text-center text-muted-foreground text-sm">
          {readOnly ? "Nessuna foto disponibile per questa opportunità." : "Nessuna foto caricata. Trascina immagini qui o usa il pulsante sopra."}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {photos.map((p, i) => (
            <div key={p.id}
              draggable={!readOnly} onDragStart={() => !readOnly && onDragStart(i)} onDragOver={onDragOver} onDrop={() => !readOnly && onDrop(i)}
              className={`group relative bg-muted rounded-md overflow-hidden border ${p.is_cover ? "border-primary ring-2 ring-primary/30" : "border-border/60"} ${readOnly ? "" : "cursor-grab active:cursor-grabbing"}`}
              data-testid={`photo-${p.id}`}
            >
              <div className="aspect-[4/3] bg-muted">
                <img src={`${API}/files/${p.storage_path}`} alt={p.caption || p.original_filename} className="w-full h-full object-cover" />
              </div>
              {p.is_cover && (
                <div className="absolute top-2 left-2 bg-primary text-primary-foreground text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1">
                  <Star className="w-2.5 h-2.5" /> Copertina
                </div>
              )}
              {!readOnly && (
                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {!p.is_cover && (
                    <button onClick={() => setCover(p.id)} className="w-7 h-7 bg-background/90 backdrop-blur rounded-md flex items-center justify-center hover:bg-primary hover:text-primary-foreground" title="Imposta copertina" data-testid={`cover-${p.id}`}>
                      <Star className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button onClick={() => del(p.id)} className="w-7 h-7 bg-background/90 backdrop-blur rounded-md flex items-center justify-center hover:bg-destructive hover:text-destructive-foreground" title="Elimina" data-testid={`del-photo-${p.id}`}>
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
              {!readOnly && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <GripVertical className="w-3.5 h-3.5 text-white/70" />
                </div>
              )}
              <div className="p-2">
                {readOnly ? (
                  p.caption ? <p className="text-xs text-muted-foreground truncate">{p.caption}</p> : null
                ) : captionEdit[p.id] !== undefined ? (
                  <div className="flex gap-1">
                    <Input value={captionEdit[p.id]} onChange={(e) => setCaptionEdit((s) => ({ ...s, [p.id]: e.target.value }))} className="h-7 text-xs" autoFocus />
                    <Button size="sm" className="h-7 px-2 text-xs" onClick={() => saveCaption(p.id)}>OK</Button>
                  </div>
                ) : (
                  <button onClick={() => setCaptionEdit((s) => ({ ...s, [p.id]: p.caption || "" }))}
                    className="text-xs text-muted-foreground hover:text-foreground text-left w-full truncate" data-testid={`caption-${p.id}`}>
                    {p.caption || "+ Aggiungi didascalia"}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
